import React from 'react';
import { getCookie } from '../common/cookie';
import { useConfig } from './useConfig';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useAuth } from './useAuth';
import useApiWrapper from '../services/api/apiWrapper';
import { useLocation } from 'react-router-dom';
import PopupModal from '../components/PopupModal';
import CustomPopupForm from '../components/CustomPopupForm';
import theMethodImage from '../assets/icons/the-method.png';

const PopupContext = createContext({});
const initialPopupAndCardState = {
  popupData: [],
  shouldShowPopup: false, // Flag to check if there are events that should be displayed as popups
  shouldShowCard: false, // Flag to check if there are events that should be displayed as cards
  qualifiedEvents: [],
  qualificationsLoaded: false, // Flag to check if qualification checks have completed
  hasActiveCustomForm: function () {
    return this.qualifiedEvents.some(item => item.isCustomFormPopupActive)
  }
}


const METHOD_EVENTS = [
  {
    contentId: 'method-france-2026',
    isActive: true,
    isCardForm: true,
    externalFormPlatform: 'eventbrite',
    type: 'announcement',
    eventDetails: {
      eventbriteId: '1985047978941',
      title: 'The Method - September 2026',
      venue: 'Nice, France',
      date: 'Sep 24 - 25, 2026',
      market: 'eu',
    },
    links: { path: '' },
    resourceLinks: { main: theMethodImage },
    styles: { maxWidth: '800px' },
    text: {
      cardText: {
        chipText: '',
        cta: 'register_now',
        titleTextKey: 'The Method France',
        isQualifiedBadgeDisplayed: true,
        description: { key: 'method_event_card_description' },
      },
      popupText: { description: { key: '' }, buttonTextKey: '', titleTextKey: 'The Method France' },
    },
  },
  {
    contentId: 'method-mena-2026',
    isActive: true,
    isCardForm: true,
    externalFormPlatform: 'eventbrite',
    type: 'announcement',
    eventDetails: {
      title: 'The Method - November 2026',
      venue: 'Turkey',
      date: 'Nov 23 - 24, 2026',
      market: 'mena',
      registrationUrl: 'https://events.unicity.com/register/method-november-turkey',
    },
    links: { path: '' },
    resourceLinks: { main: theMethodImage },
    styles: { maxWidth: '800px' },
    text: {
      cardText: {
        chipText: '',
        cta: 'register_now',
        titleTextKey: 'The Method MENA',
        isQualifiedBadgeDisplayed: true,
        description: { key: 'method_event_card_description' },
      },
      popupText: { description: { key: '' }, buttonTextKey: '', titleTextKey: 'The Method MENA' },
    },
  },
]

export const PopupContextProvider = ({ children }) => {
  const { userInfo } = useAuth()
  const [isFeedbackModalSkipped, setIsFeedbackModalSkipped] = useState(getCookie(`skippedSurvey_${userInfo?.unicityId}`) === 'true');
  const [isFeedbackTaken, setIsFeedbackTaken] = useState(getCookie(`tookSurvey_${userInfo?.unicityId}`) === 'true');
  const flags = useFlags()
  const api = useApiWrapper();
  const [titleKeys] = useState({})
  const { data: qualificationsConfig } = useConfig('qualifications/regions.json')
  const regionCountries = useMemo(() => qualificationsConfig?.data?.regions?.value ?? {}, [qualificationsConfig])
  // RENAME THIS TO SOMETHING GENERAL FOR ALL EXTERNAL POPUPS
  const location = useLocation()
  const [popupAndCardState, setPopupAndCardState] = useState(initialPopupAndCardState)
  const showCustomForm = popupAndCardState.hasActiveCustomForm()
  const isQualifyingEndpointCalledOnce = useRef(false)
  const isApiCallInProgressRef = useRef(false)

  // Holds the popup and card logic
  // LD flag shape: { eu: boolean, mena: boolean }
  // Full event config lives in METHOD_EVENTS; regionCountries maps market keys to country codes
  // regionCountries is fetched from office.config (qualifications/markets.json) with hardcoded fallback
  useEffect(() => {
    if (!userInfo?.loggedIn || !flags?.officeEventsQualifications) return

    const activeMarkets = flags.officeEventsQualifications
    const userCountry = userInfo?.countryCode

    const eligibleEvents = METHOD_EVENTS.reduce((acc, event) => {
      const market = event.eventDetails?.market
      if (!activeMarkets[market]) return acc
      if (!regionCountries[market]?.includes(userCountry)) return acc
      let merged = { ...event }
      if (userInfo?.customAttributes?.popup?.[event.contentId]) {
        merged = { ...merged, ...userInfo.customAttributes.popup[event.contentId] }
      }
      acc.push(merged)
      return acc
    }, [])

    if (eligibleEvents.length === 0) return

    setPopupAndCardState(prev => {
      let newPopupData = [...prev.popupData]
      let changed = false

      eligibleEvents.forEach(event => {
        const hasPopupData = newPopupData.some(item => item.contentId === event.contentId)
        if (!hasPopupData) {
          newPopupData = [...newPopupData, event]
          changed = true
        }
      })

      if (!changed) return prev
      return { ...prev, popupData: newPopupData }
    })
  }, [flags?.officeEventsQualifications, userInfo, regionCountries])

  useEffect(() => {
    // Cleanup, to ensure that after logout we will start with clean slate
    return () => {
      setPopupAndCardState(initialPopupAndCardState)
      isQualifyingEndpointCalledOnce.current = false
    }
  }, [])

  useEffect(() => {
    const eventData = popupAndCardState.popupData
    if (eventData.length === 0 || !userInfo.loggedIn) return

    // Should have a checking here if there are source based events (NOTE FOR FUTURE ITERATIONS)
    // If not, there is no need to poll the qualifications endpoint/s
    if (!isQualifyingEndpointCalledOnce.current) {
      getQualifications()

      return
    }

    // If the user is qualified in all available events, there is no need to poll
    const qualifiedEvents = popupAndCardState.qualifiedEvents
    if (qualifiedEvents.length === 0 || qualifiedEvents.every(item => item.isQualified)) return

    // Polling to get the qualifications
    // Every 30 minutes
    const intervalForQualificationCheck = setInterval(async () => {
      await getQualifications()
    }, 1800000) // 30 minutes

    return () => {
      clearInterval(intervalForQualificationCheck)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQualifyingEndpointCalledOnce.current, popupAndCardState, userInfo.loggedIn])

  // Polling function to get the qualifications
  const getQualifications = async () => {
    if (!isQualifyingEndpointCalledOnce.current) {
      isQualifyingEndpointCalledOnce.current = true
    }
    const distributorID = userInfo?.unicityId
    const eventData = popupAndCardState.popupData
    // can be removed once Colby's method is done
    const promises = []
    const hasMethodEvent = eventData.some(item => item.contentId.includes('method-sept2025'))

    if (hasMethodEvent) {
      promises.push(api.get(`customers/methodQualified?customerId=${distributorID}`))
    }

    // In reality, if all events are source based, we'll just use map
    // to get the sourceName from the launchdarkly popup data
    const sourceNames = eventData.reduce((acc, item) => {
      if (item.contentId.includes('method-sept2025')) {
        return acc
      }
      if (item.sourceName) {
        acc.push(item.sourceName)
      }

      return acc
    }, [])

    if (sourceNames.length > 0) {
      promises.push(api.get(`${process.env.REACT_APP_OFFICE_BE_PH_URL}/v1/user/${userInfo?.unicityId}/qualifications`, {
        params: {
          tabs: sourceNames.join(',')
        },
        addPrefix: false,
        headers: {
          'Content-Type': 'application/json',
        }
      }))
    }

    // Eventbrite market events — new qualifications endpoint
    const eventbriteMarketEvents = eventData.filter(item =>
      item.contentId.includes('method-') &&
      item.externalFormPlatform === 'eventbrite' &&
      item.eventDetails?.market
    )

    const eventbriteMarketPromises = eventbriteMarketEvents.map(event =>
      api.get(`${process.env.REACT_APP_OFFICE_BE_PH_URL}/v1/user/${distributorID}/qualifications`, {
        params: { market: event.eventDetails.market },
        addPrefix: false,
        headers: { 'Content-Type': 'application/json' },
      }).then(response => ({ contentId: event.contentId, platform: event.externalFormPlatform, response }))
    )

    // Terminate early if there are no promises, this equated to no qualifying events
    if (promises.length === 0 && eventbriteMarketPromises.length === 0) {
      setPopupAndCardState(prev => ({
        ...prev,
        qualificationsLoaded: true,
        shouldShowPopup: prev.popupData.some(item => !item.isClosed && (!item.isCardForm && !item.isCardByDefault)),
        shouldShowCard: prev.popupData.some(item => (!item.isClosed && item.isCardForm) || (item.isCardByDefault))
      }))
      return
    }

    try {
      const [legacyResults, marketResults] = await Promise.all([
        promises.length > 0 ? Promise.allSettled(promises) : Promise.resolve([]),
        Promise.allSettled(eventbriteMarketPromises)
      ])

      if (promises.length > 0) {
        setQualifiedEvents(legacyResults)
      }

      if (marketResults.length > 0) {
        setPopupAndCardState(prev => {
          const currentQualifications = [...prev.qualifiedEvents]
          marketResults.forEach(result => {
            if (result.status !== 'fulfilled') return
            const { contentId, platform, response } = result.value
            const { isQualified, isRegistered } = response.data.data
            const index = currentQualifications.findIndex(item => item.id === contentId)
            if (index === -1) {
              currentQualifications.push({ id: contentId, isQualified, isRegistered, isCustomFormPopupActive: false, platform })
            } else {
              currentQualifications[index] = { ...currentQualifications[index], isQualified, isRegistered }
            }
          })

          const qualifiedIds = currentQualifications.filter(item => item.isQualified).map(item => item.id)
          const filteredPopupData = prev.popupData.filter(item => {
            if (!item.eventDetails?.market) return true
            return qualifiedIds.includes(item.contentId)
          })

          return {
            ...prev,
            qualifiedEvents: currentQualifications,
            popupData: filteredPopupData,
            qualificationsLoaded: true,
            shouldShowCard: filteredPopupData.some(item => (!item.isClosed && item.isCardForm) || item.isCardByDefault),
            shouldShowPopup: filteredPopupData.some(item => !item.isClosed && (!item.isCardForm && !item.isCardByDefault)),
          }
        })
      }
    } catch (error) {
      console.error('[PopupContext] Error in getQualifications:', error)
    }
  }

  // Set qualifications
  // after the method vegas is removed, the events should be event only and the
  // type should be an object
  const setQualifiedEvents = (events) => {
    const [methodQualifiedResponse, qualificationsResponse] = events

    // We need to store it in an array first since we'll save this in a
    // cookie too
    const currentQualifications = [...popupAndCardState.qualifiedEvents]
    // This if is temporary since we'll remove the method-sept2025 event
    if (methodQualifiedResponse.status === 'fulfilled') {
      if (methodQualifiedResponse.value.data.qualified) {
        const index = currentQualifications?.findIndex(item => item.id === 'method-sept2025')
        // If there are no qualified events, add the method-sept2025 event
        if (index === -1) {
          currentQualifications.push({
            id: 'method-sept2025',
            isQualified: methodQualifiedResponse.value.data.qualified,
            isCustomFormPopupActive: false,
            platform: 'bizzabo'
          })
        } else {
          // If there are qualified events, update the method-sept2025 event
          currentQualifications[index] = {
            ...currentQualifications[index],
            isQualified: methodQualifiedResponse.value.data.qualified
          }
        }
      }
    }
    if (qualificationsResponse.status === 'fulfilled') {
      // Only process qualification data if there are items and at least one is qualified
      if (qualificationsResponse.value.data.data &&
        !qualificationsResponse.value.data.data.every(item => !item.isQualified)) {
        const qualificationResponseData = qualificationsResponse.value.data.data
        qualificationResponseData.forEach(item => {
          if (!item.isQualified) return
          const event = popupAndCardState.popupData.find(event => event.sourceName === item.sourceName)
          const eventId = event?.contentId
          const platform = event?.externalFormPlatform || null
          const index = currentQualifications.findIndex(event => event.id === eventId)
          if (index === -1) {
            currentQualifications.push({
              id: eventId,
              isQualified: item.isQualified,
              isCustomFormPopupActive: false,
              platform
            })
          } else {
            currentQualifications[index] = {
              ...currentQualifications[index],
              isQualified: item.isQualified
            }
          }
        })
      }
    }

    const currentQualficationsIds = currentQualifications.map(item => item.id)
    // If all isQualifed is true, save to a counter in localStorage so that
    // we can skip the qualifications polling
    setPopupAndCardState(prev => {
      const filteredPopupData = prev.popupData.filter(item => {
        if (!item.sourceName) return true
        return currentQualficationsIds.includes(item.contentId)
      })

      const shouldShowPopup = filteredPopupData.some(item => !item.isClosed && (!item.isCardForm && !item.isCardByDefault))
      const shouldShowCard = filteredPopupData.some(item => (!item.isClosed && item.isCardForm) || (item.isCardByDefault))

      return {
        ...prev,
        qualifiedEvents: currentQualifications,
        popupData: filteredPopupData,
        qualificationsLoaded: true,
        shouldShowPopup,
        shouldShowCard
      }
    })
  }

  return (
    <PopupContext.Provider value={{
      isFeedbackModalSkipped,
      setIsFeedbackModalSkipped,
      isFeedbackTaken,
      setIsFeedbackTaken,
      titleKeys,
      popupAndCardState,
      setPopupAndCardState,
      isApiCallInProgressRef
    }}>
      {children}
      {
        (!location.pathname.includes("login") && (userInfo && Object.keys(userInfo).length > 0)) && (
          <>
            {
              (popupAndCardState.shouldShowPopup && popupAndCardState.qualificationsLoaded && !showCustomForm) && (
                <PopupModal />
              )
            }
            {showCustomForm && <CustomPopupForm isOpen={showCustomForm} />}
          </>
        )
      }
    </PopupContext.Provider>
  )
}

export const usePopupContext = () => {
  const ctx = useContext(PopupContext);

  if (!ctx) throw new Error('Error in PopupContext');

  return ctx;
}